import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { api } from "@/utils/api-client";
import { Logo } from "./logo";

type LegalLink = { l: string; to: string };

export function Footer() {
  const [legalLinks, setLegalLinks] = useState<LegalLink[]>([]);

  useEffect(() => {
    // Only show legal pages that actually exist (published) in admin.
    api.silent
      .get<Array<{ title: string; slug: string }>>("/api/public/legal")
      .then((pages) => {
        if (Array.isArray(pages)) {
          setLegalLinks(pages.map((p) => ({ l: p.title, to: `/legal?tab=${p.slug}` })));
        }
      })
      .catch(() => { /* FAQ-only fallback */ });
  }, []);

  const legalColLinks: LegalLink[] = [
    { l: "FAQ", to: "/legal?tab=faq" },
    ...legalLinks,
  ];

  return (
    <footer className="relative border-t border-border bg-surface">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-5">
        <div className="md:col-span-2">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <Logo full className="h-7 w-[88px]" />
          </Link>
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">
            Rental management for India&rsquo;s self-drive bikes, scooters and cars. Web, Android and iOS.
          </p>
          <div className="mt-6 flex gap-2">
            <a
              href="https://instagram.com/rentos.tech/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="RentOS on Instagram"
              className="grid h-9 w-9 place-items-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </div>

        <FooterCol
          title="Product"
          links={[
            { l: "Features", to: "/features" },
            { l: "Pricing", to: "/pricing" },
          ]}
        />
        <FooterCol
          title="Company"
          links={[
            { l: "About", to: "/about" },
            { l: "Blog", to: "/blog" },
            { l: "Contact", to: "/contact" },
          ]}
        />
        <FooterCol title="Legal & help" links={legalColLinks} />
      </div>

      <div className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm text-muted-foreground sm:flex-row sm:px-6">
          <p>© {new Date().getFullYear()} RentOS. Built in India.</p>
          <p>For owner-run rental businesses.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { l: string; to: string }[] }) {
  return (
    <div>
      <h4 className="text-sm font-semibold tracking-tight">{title}</h4>
      <ul className="mt-4 space-y-2.5 text-sm text-muted-foreground">
        {links.map((l) => (
          <li key={l.l}>
            <Link to={l.to} className="transition-colors hover:text-foreground">
              {l.l}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
